import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Select from 'react-select';
import Loader from '../../shared/component/Loader';
import apiServices from '../../services/service';
import recordsVisibilityFlow from '../../assets/img/system hirercachy.png';
import requestPermissionFlow from '../../assets/img/image.png';

const EXCLUSIVE_OPTIONS = [
  { value: 'own', label: 'Assigned records only', description: 'Only records the user owns', icon: 'bi bi-person' },
  { value: 'all', label: 'All eligible records', description: 'Every record across the org', icon: 'bi bi-building' },
];

const EXCLUSIVE_VALUES = EXCLUSIVE_OPTIONS.map((o) => o.value);

const CITY_OPTIONS = [
  { value: 'new_york', label: 'New York' },
  { value: 'los_angeles', label: 'Los Angeles' },
  { value: 'chicago', label: 'Chicago' },
  { value: 'houston', label: 'Houston' },
  { value: 'phoenix', label: 'Phoenix' },
  { value: 'philadelphia', label: 'Philadelphia' },
  { value: 'san_antonio', label: 'San Antonio' },
  { value: 'san_diego', label: 'San Diego' },
  { value: 'dallas', label: 'Dallas' },
  { value: 'san_jose', label: 'San Jose' },
  { value: 'austin', label: 'Austin' },
  { value: 'jacksonville', label: 'Jacksonville' },
  { value: 'denver', label: 'Denver' },
  { value: 'seattle', label: 'Seattle' },
  { value: 'boston', label: 'Boston' },
];

// Maps a GeographyNode/OrgUnitNode API record ({ id, nodeType, parentId, name, path }) to a select option
const toNodeOption = (node) => ({
  code: node.id,
  label: `${node.nodeType} - ${node.name} - ${node.path}`,
});

const multiSelectStyles = {
  container: (base) => ({ ...base, fontSize: '0.85rem' }),
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#456ABB' : base.borderColor,
    boxShadow: state.isFocused ? '0 0 0 1px #456ABB' : 'none',
    '&:hover': { borderColor: '#456ABB' },
  }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#C2DBFF' : state.isFocused ? '#f0f5ff' : 'white',
    color: '#222',
  }),
  multiValue: (base) => ({ ...base, backgroundColor: '#dde8ff', borderRadius: '4px' }),
  multiValueLabel: (base) => ({ ...base, fontSize: '0.78rem', color: '#275ACA' }),
};

const MULTI_SECTIONS = [
  { key: 'smartBuckets', icon: 'bi bi-list', label: 'Smart Bucket(s)', description: 'Eligible records in bucket as per bucket(L1,L2,L3) & user access level(L1,L2,L3).\n Note:-\n Bucket owner can see all records of their bucket irrespective of the setting mentioned here.', options: ['Eligible'] },
  { key: 'team', icon: 'bi bi-people', label: 'Team(s)', description: 'Records visibility, where user is a team member.\n Note:- \n Team owner and Team captain can see all records of their team irrespective of the setting mentioned here.\nTeam is a ad/hoc explicit grouping of members, separate from "structural" hierarchies.', options: ['Assigned', 'All'] }
];

// Defined at module level — stable reference, no remount
const ExclusiveCard = ({ opt, recordVisibility, onToggle }) => {
  const isSelected = recordVisibility.includes(opt.value);
  return (
    <div
      className={`record-visibility-card${isSelected ? ' selected' : ''}`}
      onClick={() => onToggle(opt.value)}
      style={{ cursor: 'pointer', flex: '1 1 0', minWidth: 0 }}
    >
      <div className="record-visibility-icon">
        <i className={opt.icon} />
      </div>
      <div className="record-visibility-text">
        <span className="record-visibility-label">{opt.label}</span>
        <span className="record-visibility-desc">{opt.description}</span>
      </div>
    </div>
  );
};

const ManageRecordsVisibility = ({ rowData, setUpdateListFlag }) => {
  // Modal open/close
  const [showPopover, setShowPopover] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showFlowDiagram, setShowFlowDiagram] = useState(false);
  const [showRequestFlowDiagram, setShowRequestFlowDiagram] = useState(false);

  // Exclusive scope (None / Own / All)
  const [recordVisibility, setRecordVisibility] = useState(['Assigned']);

  // Team & Smart Buckets scope
  const [teamScope, setTeamScope] = useState('');
  const [smartBucketsScope, setSmartBucketsScope] = useState('');

  const [loading, setLoading] = useState(false);

  // Lead Location
  const [selectedCities, setSelectedCities] = useState([]);

  // GeographyNode / OrgUnitNode — sourced from the user record (rowData, from getUserList) and updated via updateUser
  const [selectedLobState, setSelectedLobState] = useState(null);
  const [selectedOrgUnitNode, setSelectedOrgUnitNode] = useState(null);
  const [geographyOptions, setGeographyOptions] = useState([]);
  const [orgUnitOptions, setOrgUnitOptions] = useState([]);
  const [nodesLoading, setNodesLoading] = useState(false);
  const nodesFetchedRef = useRef(false);

  // Fetch the GeographyNode / OrgUnitNode reference lists the first time this row's modal opens
  useEffect(() => {
    if (!showPopover || nodesFetchedRef.current) return;
    nodesFetchedRef.current = true;
    setNodesLoading(true);
    Promise.all([apiServices.GetGeographyNodes(), apiServices.GetOrgUnitNodes()])
      .then(([geoResponse, orgResponse]) => {
        setGeographyOptions((geoResponse?.data?.geographyNodes || []).map(toNodeOption));
        setOrgUnitOptions((orgResponse?.data?.orgUnitNodes || []).map(toNodeOption));
      })
      .catch((err) => {
        console.error('Error fetching GeographyNode/OrgUnitNode lists:', err);
        nodesFetchedRef.current = false;
      })
      .finally(() => setNodesLoading(false));
  }, [showPopover]);

  // Bind the current GeographyNode selection from rowData.geoScopeNodeId once the option list loads
  useEffect(() => {
    if (!geographyOptions.length) return;
    const match = geographyOptions.find((o) => String(o.code) === String(rowData?.geoScopeNodeId));
    setSelectedLobState(match || null);
  }, [rowData?.geoScopeNodeId, geographyOptions]);

  // Bind the current OrgUnitNode selection from rowData.orgScopeNodeId once the option list loads
  useEffect(() => {
    if (!orgUnitOptions.length) return;
    const match = orgUnitOptions.find((o) => String(o.code) === String(rowData?.orgScopeNodeId));
    setSelectedOrgUnitNode(match || null);
  }, [rowData?.orgScopeNodeId, orgUnitOptions]);

  // Fetch existing permissions when modal opens
  useEffect(() => {
    if (!showPopover || !rowData?.userId) return;
    setLoading(true);
    apiServices
      .getRecordsVisibilityByUserId(rowData.userId)
      .then((response) => {
        const rv = response?.data?.recordVisibility;
        if (!rv) return;

        const general = rv.generalRecordVisibility?.toLowerCase();
        if (general) setRecordVisibility([general]);

        const team = rv.teamRecordVisibility?.toLowerCase();
        setTeamScope(team || '');

        const bucket = rv.bucketRecordVisibility?.toLowerCase();
        setSmartBucketsScope(bucket || '');
      })
      .catch((err) => console.error('Error fetching record visibility:', err))
      .finally(() => setLoading(false));
  }, [showPopover, rowData?.userId]);

  const handleOpen = () => setShowPopover(true);
  const handleClose = () => setShowPopover(false);

  const handleVisibilityToggle = (value) => {
    if (EXCLUSIVE_VALUES.includes(value)) {
      setRecordVisibility((prev) => {
        const withoutExclusive = prev.filter((v) => !EXCLUSIVE_VALUES.includes(v));
        return prev.includes(value) ? withoutExclusive : [...withoutExclusive, value];
      });
    } else {
      setRecordVisibility((prev) =>
        prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
      );
    }
  };

  const handleScopeClick = (setter, current, value) => {
    setter(current === value ? '' : value);
  };

  const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : null;

  const handleSubmit = () => {
    const generalValue = recordVisibility.find((v) => EXCLUSIVE_VALUES.includes(v));
    if (!generalValue) {
      toast.error('Please select a scope option.', { position: 'top-right' });
      return;
    }
    setLoading(true);
    const visibilityPayload = {
      userId: rowData?.userId,
      generalRecordVisibility: capitalize(generalValue),
      teamRecordVisibility: teamScope ? capitalize(teamScope) : null,
      bucketRecordVisibility: smartBucketsScope ? capitalize(smartBucketsScope) : null,
    };
    // updateUser only accepts this fixed set of keys — the rest come from rowData (getUserList) untouched
    const userUpdatePayload = {
      userId: rowData?.userId,
      email: rowData?.email,
      phoneNumber: rowData?.phoneNumber,
      empId: rowData?.empId,
      orgScopeNodeId: selectedOrgUnitNode?.code ?? null,
      geoScopeNodeId: selectedLobState?.code ?? null,
    };
    Promise.all([
      apiServices.updateRecordsVisibility(visibilityPayload),
      apiServices.updateUser(userUpdatePayload),
    ])
      .then(() => {
        toast.success('Records visibility updated successfully!', { position: 'top-right' });
        setUpdateListFlag?.(true);
        handleClose();
      })
      .catch((err) => {
        console.error('Error updating record visibility:', err);
        toast.error('Failed to update records visibility.', { position: 'top-right' });
      })
      .finally(() => setLoading(false));
  };

  const scopeState = { team: teamScope, smartBuckets: smartBucketsScope };
  const scopeSetters = { team: setTeamScope, smartBuckets: setSmartBucketsScope };

  return (
    <>
      {/* Eye icon trigger */}
      <div className="action-menu">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          fill="currentColor"
          className="bi bi-eye-fill"
          viewBox="0 0 16 16"
          onClick={handleOpen}
          style={{ cursor: 'pointer' }}
          title="Manage Records Visibility"
        >
          <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0" />
          <path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8m8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7" />
        </svg>
      </div>

      <Modal
        show={showPopover}
        onHide={handleClose}
        backdrop="static"
        id="manage_records_visibility"
        size={isExpanded ? undefined : 'lg'}
        fullscreen={isExpanded ? true : undefined}
        dialogClassName={isExpanded ? 'mrv-sidebar-offset' : undefined}
      >
        {loading && <Loader />}

        {/* Header */}
        <Modal.Header
          closeButton={false}
          style={{ padding: '0.85rem 1.25rem', borderBottom: '1px solid #e5e7eb' }}
        >
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 600 }}>
            Manage Records Visibility
          </Modal.Title>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
            <button
              onClick={() => setIsExpanded((p) => !p)}
              title={isExpanded ? 'Restore' : 'Expand'}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', display: 'flex', alignItems: 'center', color: '#555' }}
            >
              {isExpanded ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 16.558 16.558">
                  <path d="M3.763,11.391H8.339m0,0v4.577m0-4.577L3,16.73M15.967,8.339H11.391m0,0V3.763m0,4.577L16.73,3" transform="translate(-1.586 -1.586)" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 16.561 16.561">
                  <path d="M11.392,8.341,16.733,3m0,0H12.155m4.578,0V7.578M8.341,11.392,3,16.733m0,0H7.578M3,16.733V12.155" transform="translate(-1.586 -1.586)" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                </svg>
              )}
            </button>
            <button
              onClick={handleClose}
              title="Close"
              aria-label="Close"
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem', lineHeight: 1, color: '#888', padding: '2px 6px' }}
            >
              &times;
            </button>
          </div>
        </Modal.Header>

        {/* Body */}
        <Modal.Body style={{ padding: '1.25rem', overflowY: 'auto', maxHeight: '70vh' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#222' }}>User:</span>
            <span style={{ fontSize: '0.85rem', color: '#555' }}>
              {rowData?.display_name || `${rowData?.firstName || ''} ${rowData?.lastName || ''}`.trim() || '—'}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
            <span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#222' }}>Access Level:</span>
            <span style={{ fontSize: '0.85rem', color: '#555' }}>{rowData?.accessLevel || '—'}</span>
          </div>

          <p style={{ fontSize: '0.83rem', color: '#888', margin: '0 0 18px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
            The default set of records this user can see.
            <i
              className="bi bi-info-circle"
              role="button"
              tabIndex={0}
              title="How record visibility is resolved"
              onClick={() => setShowFlowDiagram(true)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setShowFlowDiagram(true)}
              style={{ cursor: 'pointer', color: '#456ABB', fontSize: '0.95rem' }}
            />
            <i
              className="bi bi-diagram-3"
              role="button"
              tabIndex={0}
              title="How action & field permissions apply to a request"
              onClick={() => setShowRequestFlowDiagram(true)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setShowRequestFlowDiagram(true)}
              style={{ cursor: 'pointer', color: '#456ABB', fontSize: '0.95rem' }}
            />
          </p>

          {/* View section */}
          <div
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: '10px',
              padding: '16px',
              marginBottom: '20px',
              backgroundColor: '#fcfcfd',
            }}
          >
            <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#222', marginBottom: '14px' }}>
              View
            </div>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              {EXCLUSIVE_OPTIONS.map((opt) => (
                <ExclusiveCard
                  key={opt.value}
                  opt={opt}
                  recordVisibility={recordVisibility}
                  onToggle={handleVisibilityToggle}
                />
              ))}
            </div>

            {/* Bucket & Team — part of the View section, no separate heading */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {MULTI_SECTIONS.map(({ key, icon, label, description, options }) => {
                const currentScope = scopeState[key];
                const setter = scopeSetters[key];
                const isActive = currentScope !== '';

                return (
                  <div
                    key={key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: `1.5px solid ${isActive ? '#456ABB' : '#e0e4ea'}`,
                      backgroundColor: isActive ? '#f0f5ff' : '#fafafa',
                      transition: 'border-color 0.15s, background-color 0.15s',
                    }}
                  >
                    {/* Icon badge */}
                    <div
                      style={{
                        width: '36px', height: '36px', borderRadius: '8px', flexShrink: 0,
                        backgroundColor: isActive ? '#dde8ff' : '#eef0f4',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'background-color 0.15s',
                      }}
                    >
                      <i className={icon} style={{ fontSize: '1rem', color: isActive ? '#456ABB' : '#888' }} />
                    </div>

                    {/* Label */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#222' }}>{label}</div>
                      <div style={{ fontSize: '0.78rem', color: '#777', marginTop: '2px', whiteSpace: 'pre-line' }}>{description}</div>
                    </div>

                    {/* All / Own pill buttons */}
                    <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                      {options.map((option) => {
                        const val = option.toLowerCase();
                        const checked = currentScope === val;
                        return (
                          <button
                            key={val}
                            type="button"
                            onClick={() => handleScopeClick(setter, currentScope, val)}
                            style={{
                              padding: '5px 18px',
                              borderRadius: '20px',
                              border: checked ? 'none' : '1.5px solid #d0d5dd',
                              background: checked
                                ? 'linear-gradient(90deg, rgba(40, 22, 141, 1) 0%, rgba(96, 18, 120, 1) 100%)'
                                : '#fff',
                              color: checked ? '#fff' : '#555',
                              fontSize: '0.82rem',
                              fontWeight: checked ? 600 : 400,
                              cursor: 'pointer',
                              transition: 'all 0.15s',
                              outline: 'none',
                            }}
                          >
                            {option}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {false && (
            <>
              <div style={{ borderTop: '1px solid #e9ecef', margin: '16px 0' }} />

              {/* Lead Location */}
              <div>
                <label
                  style={{
                    display: 'block',
                    fontWeight: 600,
                    fontSize: '0.88rem',
                    color: '#222',
                    marginBottom: '6px',
                  }}
                >
                  Lead Location
                </label>
                <p style={{ fontSize: '0.78rem', color: '#777', margin: '0 0 8px 0' }}>
                  Restrict visibility to leads from specific cities.
                </p>
                <Select
                  isMulti
                  placeholder="Select cities..."
                  value={selectedCities}
                  onChange={setSelectedCities}
                  options={CITY_OPTIONS}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={multiSelectStyles}
                />
              </div>
            </>
          )}

          {/* Filters section */}
          <div
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: '10px',
              padding: '16px',
              backgroundColor: '#fcfcfd',
            }}
          >
            <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#222', marginBottom: '14px' }}>
              Filters
            </div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label
                  style={{ display: 'block', fontWeight: 600, fontSize: '0.88rem', color: '#222', marginBottom: '6px' }}
                >
                  GeographyNode
                </label>
                <Select
                  placeholder="Select GeographyNode..."
                  value={selectedLobState}
                  onChange={setSelectedLobState}
                  options={geographyOptions}
                  getOptionValue={(o) => o.code}
                  isLoading={nodesLoading}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={multiSelectStyles}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label
                  style={{ display: 'block', fontWeight: 600, fontSize: '0.88rem', color: '#222', marginBottom: '6px' }}
                >
                  OrgUnitNode
                </label>
                <Select
                  placeholder="Select OrgUnitNode..."
                  value={selectedOrgUnitNode}
                  onChange={setSelectedOrgUnitNode}
                  options={orgUnitOptions}
                  getOptionValue={(o) => o.code}
                  isLoading={nodesLoading}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={multiSelectStyles}
                />
              </div>
            </div>
          </div>
        </Modal.Body>

        {/* Footer */}
        <Modal.Footer style={{ borderTop: '1px solid #e5e7eb', padding: '0.75rem 1.25rem' }}>
          <button type="button" className="btn btn-primary" onClick={handleSubmit}>
            Apply
          </button>
          <button type="button" className="btn btn-outline-primary" onClick={handleClose}>
            Cancel
          </button>
        </Modal.Footer>
      </Modal>

      {/* Record visibility resolution flow diagram */}
      <Modal show={showFlowDiagram} onHide={() => setShowFlowDiagram(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 600 }}>
            How Record Visibility Is Resolved
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ textAlign: 'center' }}>
          <img
            src={recordsVisibilityFlow}
            alt="Record visibility resolution flow"
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        </Modal.Body>
      </Modal>

      {/* Action/field permission & record visibility filtering flow diagram */}
      <Modal show={showRequestFlowDiagram} onHide={() => setShowRequestFlowDiagram(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 600 }}>
            How Action &amp; Field Permissions Apply To A Request
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ textAlign: 'center' }}>
          <img
            src={requestPermissionFlow}
            alt="Action and field permission, record visibility filtering flow"
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ManageRecordsVisibility;
